import { NgClass } from '@angular/common';
import { Component, signal } from '@angular/core';

export interface ToastMessage {
  message: string;
  type: 'success' | 'error' | 'info';
  duration?: number;
}

@Component({
  selector: 'app-toast',
  standalone: true,
  template: `
    @if (visible()) {
      <div
        class="fixed bottom-8 right-4 z-50 p-3 rounded-lg flex items-center gap-3 max-w-[500px] border border-transparent"
        [ngClass]="{
          'border-[#67A248]! bg-[#EFF9F6]': toastData().type === 'success',
          'border-[#B12121]! bg-[#FFEBE8]': toastData().type === 'error',
        }"
      >
        @if (toastData().type === 'success') {
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0ZM13.1891 6.61385L8.01965 11.9348L6.41095 10.2789C6.13393 9.9938 5.68479 9.9938 5.40777 10.2789C5.13075 10.5641 5.13075 11.0264 5.40777 11.3115L8.01965 14L14.1922 7.64645C14.4693 7.3613 14.4693 6.899 14.1922 6.61385C13.9152 6.32871 13.4661 6.32871 13.1891 6.61385Z"
              fill="#67A248"
            />
          </svg>
        }
        @if (toastData().type === 'error') {
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 0C4.47581 0 0 4.47581 0 10C0 15.5242 4.47581 20 10 20C15.5242 20 20 15.5242 20 10C20 4.47581 15.5242 0 10 0ZM14.879 12.6613C15.0806 12.8226 15.0806 13.1452 14.879 13.3468L13.3065 14.9194C13.1048 15.121 12.7823 15.121 12.621 14.9194L10 12.2581L7.33871 14.9194C7.17742 15.121 6.85484 15.121 6.65323 14.9194L5.08065 13.3065C4.87903 13.1452 4.87903 12.8226 5.08065 12.621L7.74194 10L5.08065 7.37903C4.87903 7.21774 4.87903 6.89516 5.08065 6.69355L6.69355 5.12097C6.85484 4.91935 7.17742 4.91935 7.37903 5.12097L10 7.74194L12.621 5.12097C12.7823 4.91935 13.1048 4.91935 13.3065 5.12097L14.879 6.69355C15.0806 6.89516 15.0806 7.21774 14.879 7.37903L12.2581 10L14.879 12.6613Z"
              fill="#B12121"
            />
          </svg>
        }
        @if (toastData().type === 'info') {
          <svg
            class="w-6 h-6 shrink-0 stroke-bleu-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
        }
        <span class="text-sm">{{ toastData().message }}</span>
        <button class="cursor-pointer" (click)="hide()">
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 5L1 9M5 5L1 1L5 5ZM5 5L9 9L5 5ZM5 5L9 1L5 5Z"
              stroke="#333333"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    }
  `,
  imports: [NgClass],
})
export class Toast {
  visible = signal(false);
  toastData = signal<ToastMessage>({
    message: '',
    type: 'info',
    duration: 3000,
  });

  private timeoutId: number | null = null;

  show(data: ToastMessage): void {
    // Clear existing timeout
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }

    this.toastData.set(data);
    this.visible.set(true);

    const duration = data.duration ?? 5000;
    this.timeoutId = window.setTimeout(() => {
      this.hide();
    }, duration);
  }

  hide(): void {
    this.visible.set(false);
    this.timeoutId = null;
  }
}
